import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Clock } from "lucide-react";
import { toast } from "sonner";

export const DAYS_IT = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
export const DAYS_EN = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function OpeningHours() {
  const { t, lang } = useLanguage();
  const [hours, setHours] = useState(null);
  const days = lang === "it" ? DAYS_IT : DAYS_EN;

  useEffect(() => {
    api.get("/settings").then((r) => setHours(r.data.opening_hours)).catch(() => {});
  }, []);

  const set = (d, k, v) => setHours((h) => ({ ...h, [d]: { ...h[d], [k]: v } }));

  const save = async () => {
    try {
      await api.put("/settings", { opening_hours: hours });
      toast.success(t("settings_saved"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!hours) return null;

  return (
    <div data-testid="opening-hours-panel" className="mb-4 bg-white border border-[#E8DFD5] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-[#D97706]" />
        <p className="text-xs font-semibold text-[#4A2C2A]">{t("opening_hours")}</p>
      </div>
      <div className="space-y-2">
        {days.map((name, d) => {
          const row = hours[String(d)];
          return (
            <div key={d} data-testid={`hours-row-${d}`} className="flex items-center gap-2 text-sm">
              <span className="w-20 shrink-0 font-medium text-[#1F1615] text-xs sm:text-sm">{name}</span>
              <Switch data-testid={`hours-open-switch-${d}`} checked={!row.closed} onCheckedChange={(v) => set(String(d), "closed", !v)} />
              {row.closed ? (
                <span className="text-xs text-red-600 font-semibold">{t("closed_day")}</span>
              ) : (
                <>
                  <Input data-testid={`hours-open-${d}`} type="time" value={row.open} onChange={(e) => set(String(d), "open", e.target.value)} className="flex-1 min-w-0 h-8 px-1.5 text-xs bg-white border-[#E8DFD5]" />
                  <span className="text-[#786C66]">–</span>
                  <Input data-testid={`hours-close-${d}`} type="time" value={row.close} onChange={(e) => set(String(d), "close", e.target.value)} className="flex-1 min-w-0 h-8 px-1.5 text-xs bg-white border-[#E8DFD5]" />
                </>
              )}
            </div>
          );
        })}
      </div>
      <Button data-testid="save-hours-btn" size="sm" onClick={save} className="mt-3 rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7]">
        {t("save")}
      </Button>
    </div>
  );
}
